import type { FormField } from './FormBuilder';

export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  fields: FormField[];
}

// Predefined insurance-specific form templates
const TEMPLATES: TemplateMetadata[] = [
  {
    id: 'adventure-shield',
    name: 'Adventure Shield',
    description: 'Streamlined application for adventure sports and powersports coverage',
    fields: [
      {
        name: 'company-name',
        fieldId: 'company_name',
        type: 'text',
        label: 'Company Name',
        placeholder: 'Enter your company name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 150,
        },
      },
      {
        name: 'first-name',
        fieldId: 'first_name',
        type: 'text',
        label: 'First Name',
        placeholder: 'Enter your first name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 50,
        },
      },
      {
        name: 'last-name',
        fieldId: 'last_name',
        type: 'text',
        label: 'Last Name',
        placeholder: 'Enter your last name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 50,
        },
      },
      {
        name: 'email',
        fieldId: 'email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'Enter your email address',
        required: true,
      },
      {
        name: 'powersports-units',
        fieldId: 'number_of_motorized_powersports_units',
        type: 'number',
        label: 'Number of Motorized Powersports Units',
        placeholder: 'Enter the number of units',
        required: true,
        validation: {
          min: 1,
          max: 100,
        },
      },
    ],
  },
  {
    id: 'power-sports',
    name: 'Power Sports Insurance',
    description: 'Comprehensive coverage application for motorcycles, ATVs, snowmobiles, and other power sports vehicles',
    fields: [
      {
        name: 'applicant-name',
        fieldId: 'applicant_name',
        type: 'text',
        label: 'Full Name',
        placeholder: 'Enter your full legal name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 100,
        },
      },
      {
        name: 'contact-phone',
        fieldId: 'contact_phone',
        type: 'text',
        label: 'Phone Number',
        placeholder: 'Enter your phone number',
        required: true,
      },
      {
        name: 'contact-email',
        fieldId: 'contact_email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'Enter your email address',
        required: true,
      },
      {
        name: 'vehicle-type',
        fieldId: 'vehicle_type',
        type: 'select',
        label: 'Vehicle Type',
        required: true,
        options: [
          { label: 'Motorcycle', value: 'motorcycle' },
          { label: 'ATV/UTV', value: 'atv_utv' },
          { label: 'Snowmobile', value: 'snowmobile' },
          { label: 'Personal Watercraft', value: 'personal_watercraft' },
          { label: 'Dirt Bike', value: 'dirt_bike' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        name: 'vehicle-make',
        fieldId: 'vehicle_make',
        type: 'text',
        label: 'Make',
        placeholder: 'Vehicle manufacturer',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 50,
        },
      },
      {
        name: 'vehicle-model',
        fieldId: 'vehicle_model',
        type: 'text',
        label: 'Model',
        placeholder: 'Vehicle model',
        required: true,
        validation: {
          minLength: 1,
          maxLength: 50,
        },
      },
      {
        name: 'vehicle-year',
        fieldId: 'vehicle_year',
        type: 'number',
        label: 'Year',
        placeholder: 'YYYY',
        required: true,
        validation: {
          min: 1980,
          max: new Date().getFullYear() + 1,
        },
      },
      {
        name: 'vehicle-vin',
        fieldId: 'vehicle_vin',
        type: 'text',
        label: 'VIN/Serial Number',
        placeholder: 'Enter VIN or serial number',
        required: true,
        validation: {
          minLength: 8,
          maxLength: 20,
          pattern: '^[A-HJ-NPR-Z0-9]+$',
        },
      },
      {
        name: 'vehicle-value',
        fieldId: 'vehicle_value',
        type: 'text',
        label: 'Estimated Value',
        placeholder: 'Current market value',
        required: true,
        validation: {
          pattern: '^\\$?[0-9,]+(\\.\\d{2})?$',
        },
      },
      {
        name: 'primary-use',
        fieldId: 'primary_use',
        type: 'select',
        label: 'Primary Use',
        required: true,
        options: [
          { label: 'Recreational', value: 'recreational' },
          { label: 'Transportation', value: 'transportation' },
          { label: 'Racing/Competition', value: 'racing_competition' },
          { label: 'Commercial', value: 'commercial' },
        ],
      },
      {
        name: 'storage-location',
        fieldId: 'storage_location',
        type: 'select',
        label: 'Storage Location',
        required: true,
        options: [
          { label: 'Enclosed Garage', value: 'enclosed_garage' },
          { label: 'Carport/Covered', value: 'carport_covered' },
          { label: 'Outdoor - Secured', value: 'outdoor_secured' },
          { label: 'Outdoor - Unsecured', value: 'outdoor_unsecured' },
          { label: 'Storage Facility', value: 'storage_facility' },
        ],
      },
      {
        name: 'safety-course',
        fieldId: 'safety_course',
        type: 'radio',
        label: 'Have you completed a safety course?',
        required: true,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        name: 'riding-experience',
        fieldId: 'riding_experience',
        type: 'select',
        label: 'Years of Riding Experience',
        required: true,
        options: [
          { label: 'Less than 1 year', value: 'less_than_1' },
          { label: '1-3 years', value: '1_to_3' },
          { label: '4-10 years', value: '4_to_10' },
          { label: '11-20 years', value: '11_to_20' },
          { label: 'Over 20 years', value: 'over_20' },
        ],
      },
      {
        name: 'previous-claims',
        fieldId: 'previous_claims',
        type: 'radio',
        label: 'Any claims in the past 5 years?',
        required: true,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        name: 'coverage-desired',
        fieldId: 'coverage_desired',
        type: 'select',
        label: 'Coverage Desired',
        required: true,
        options: [
          { label: 'Liability Only', value: 'liability_only' },
          { label: 'Liability + Comprehensive', value: 'liability_comprehensive' },
          { label: 'Liability + Collision', value: 'liability_collision' },
          { label: 'Full Coverage', value: 'full_coverage' },
        ],
      },
    ],
  },
  {
    id: 'marine',
    name: 'Marine Insurance',
    description: 'Comprehensive coverage for boats, yachts, and watercraft including hull, liability, and equipment',
    fields: [
      {
        name: 'owner-name',
        fieldId: 'owner_name',
        type: 'text',
        label: 'Owner Name',
        placeholder: 'Enter owner full name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 100,
        },
      },
      {
        name: 'owner-address',
        fieldId: 'owner_address',
        type: 'text',
        label: 'Address',
        placeholder: 'Enter complete address',
        required: true,
        validation: {
          minLength: 10,
          maxLength: 200,
        },
      },
      {
        name: 'contact-phone',
        fieldId: 'contact_phone',
        type: 'text',
        label: 'Phone Number',
        placeholder: 'Enter phone number',
        required: true,
      },
      {
        name: 'contact-email',
        fieldId: 'contact_email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'Enter email address',
        required: true,
      },
      {
        name: 'vessel-type',
        fieldId: 'vessel_type',
        type: 'select',
        label: 'Vessel Type',
        required: true,
        options: [
          { label: 'Sailboat', value: 'sailboat' },
          { label: 'Power Boat', value: 'power_boat' },
          { label: 'Yacht', value: 'yacht' },
          { label: 'Fishing Boat', value: 'fishing_boat' },
          { label: 'Pontoon Boat', value: 'pontoon_boat' },
          { label: 'Personal Watercraft', value: 'personal_watercraft' },
          { label: 'Commercial Vessel', value: 'commercial_vessel' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        name: 'vessel-make',
        fieldId: 'vessel_make',
        type: 'text',
        label: 'Make',
        placeholder: 'Vessel manufacturer',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 50,
        },
      },
      {
        name: 'vessel-model',
        fieldId: 'vessel_model',
        type: 'text',
        label: 'Model',
        placeholder: 'Vessel model',
        required: true,
        validation: {
          minLength: 1,
          maxLength: 50,
        },
      },
      {
        name: 'vessel-year',
        fieldId: 'vessel_year',
        type: 'number',
        label: 'Year Built',
        placeholder: 'YYYY',
        required: true,
        validation: {
          min: 1950,
          max: new Date().getFullYear() + 1,
        },
      },
      {
        name: 'vessel-length',
        fieldId: 'vessel_length',
        type: 'number',
        label: 'Length (feet)',
        placeholder: 'Overall length',
        required: true,
        validation: {
          min: 8,
          max: 1000,
        },
      },
      {
        name: 'hull-material',
        fieldId: 'hull_material',
        type: 'select',
        label: 'Hull Material',
        required: true,
        options: [
          { label: 'Fiberglass', value: 'fiberglass' },
          { label: 'Aluminum', value: 'aluminum' },
          { label: 'Steel', value: 'steel' },
          { label: 'Wood', value: 'wood' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        name: 'hull-id',
        fieldId: 'hull_id',
        type: 'text',
        label: 'Hull ID Number',
        placeholder: 'Enter hull identification number',
        required: true,
        validation: {
          minLength: 12,
          maxLength: 12,
          pattern: '^[A-Z]{3}[A-Z0-9]{5}[A-Z0-9]{2}[0-9]{2}$',
        },
      },
      {
        name: 'engine-type',
        fieldId: 'engine_type',
        type: 'select',
        label: 'Engine Type',
        required: true,
        options: [
          { label: 'Outboard', value: 'outboard' },
          { label: 'Inboard', value: 'inboard' },
          { label: 'Inboard/Outboard', value: 'inboard_outboard' },
          { label: 'Jet Drive', value: 'jet_drive' },
          { label: 'Sail Only', value: 'sail_only' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        name: 'engine-horsepower',
        fieldId: 'engine_horsepower',
        type: 'number',
        label: 'Total Horsepower',
        placeholder: 'Combined HP of all engines',
        required: false,
        validation: {
          min: 0,
          max: 10000,
        },
      },
      {
        name: 'vessel-value',
        fieldId: 'vessel_value',
        type: 'text',
        label: 'Agreed Value',
        placeholder: 'Current market value',
        required: true,
        validation: {
          pattern: '^\\$?[0-9,]+(\\.\\d{2})?$',
        },
      },
      {
        name: 'primary-use',
        fieldId: 'primary_use',
        type: 'select',
        label: 'Primary Use',
        required: true,
        options: [
          { label: 'Pleasure/Recreation', value: 'pleasure_recreation' },
          { label: 'Fishing', value: 'fishing' },
          { label: 'Water Sports', value: 'water_sports' },
          { label: 'Commercial', value: 'commercial' },
          { label: 'Charter', value: 'charter' },
        ],
      },
      {
        name: 'navigation-area',
        fieldId: 'navigation_area',
        type: 'select',
        label: 'Navigation Area',
        required: true,
        options: [
          { label: 'Inland Waters', value: 'inland_waters' },
          { label: 'Coastal Waters', value: 'coastal_waters' },
          { label: 'Great Lakes', value: 'great_lakes' },
          { label: 'Ocean/Offshore', value: 'ocean_offshore' },
          { label: 'International Waters', value: 'international_waters' },
        ],
      },
      {
        name: 'mooring-location',
        fieldId: 'mooring_location',
        type: 'text',
        label: 'Mooring/Storage Location',
        placeholder: 'Where is the vessel kept?',
        required: true,
        validation: {
          minLength: 5,
          maxLength: 150,
        },
      },
      {
        name: 'boating-experience',
        fieldId: 'boating_experience',
        type: 'select',
        label: 'Years of Boating Experience',
        required: true,
        options: [
          { label: 'Less than 1 year', value: 'less_than_1' },
          { label: '1-5 years', value: '1_to_5' },
          { label: '6-15 years', value: '6_to_15' },
          { label: '16-25 years', value: '16_to_25' },
          { label: 'Over 25 years', value: 'over_25' },
        ],
      },
      {
        name: 'previous-claims',
        fieldId: 'previous_claims',
        type: 'radio',
        label: 'Any marine claims in the past 5 years?',
        required: true,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
    ],
  },
  {
    id: 'commercial-liability',
    name: 'Commercial Liability Insurance',
    description: 'General liability coverage for businesses including professional liability, product liability, and premises coverage',
    fields: [
      {
        name: 'business-name',
        fieldId: 'business_name',
        type: 'text',
        label: 'Business Name',
        placeholder: 'Enter legal business name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 150,
        },
      },
      {
        name: 'business-address',
        fieldId: 'business_address',
        type: 'text',
        label: 'Business Address',
        placeholder: 'Enter complete business address',
        required: true,
        validation: {
          minLength: 10,
          maxLength: 200,
        },
      },
      {
        name: 'contact-person',
        fieldId: 'contact_person',
        type: 'text',
        label: 'Contact Person',
        placeholder: 'Primary contact name',
        required: true,
        validation: {
          minLength: 2,
          maxLength: 100,
        },
      },
      {
        name: 'contact-phone',
        fieldId: 'contact_phone',
        type: 'text',
        label: 'Phone Number',
        placeholder: 'Business phone number',
        required: true,
      },
      {
        name: 'contact-email',
        fieldId: 'contact_email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'Business email address',
        required: true,
      },
      {
        name: 'business-type',
        fieldId: 'business_type',
        type: 'select',
        label: 'Business Type',
        required: true,
        options: [
          { label: 'Corporation', value: 'corporation' },
          { label: 'LLC', value: 'llc' },
          { label: 'Partnership', value: 'partnership' },
          { label: 'Sole Proprietorship', value: 'sole_proprietorship' },
          { label: 'Non-Profit', value: 'non_profit' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        name: 'industry',
        fieldId: 'industry',
        type: 'select',
        label: 'Industry',
        required: true,
        options: [
          { label: 'Professional Services', value: 'professional_services' },
          { label: 'Retail', value: 'retail' },
          { label: 'Manufacturing', value: 'manufacturing' },
          { label: 'Construction', value: 'construction' },
          { label: 'Healthcare', value: 'healthcare' },
          { label: 'Technology', value: 'technology' },
          { label: 'Food Service', value: 'food_service' },
          { label: 'Transportation', value: 'transportation' },
          { label: 'Real Estate', value: 'real_estate' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        name: 'business-description',
        fieldId: 'business_description',
        type: 'textarea',
        label: 'Business Description',
        placeholder: 'Describe your business operations in detail',
        required: true,
        validation: {
          minLength: 50,
          maxLength: 1000,
        },
      },
      {
        name: 'years-in-business',
        fieldId: 'years_in_business',
        type: 'select',
        label: 'Years in Business',
        required: true,
        options: [
          { label: 'Less than 1 year', value: 'less_than_1' },
          { label: '1-2 years', value: '1_to_2' },
          { label: '3-5 years', value: '3_to_5' },
          { label: '6-10 years', value: '6_to_10' },
          { label: 'Over 10 years', value: 'over_10' },
        ],
      },
      {
        name: 'number-employees',
        fieldId: 'number_employees',
        type: 'select',
        label: 'Number of Employees',
        required: true,
        options: [
          { label: '1-5', value: '1_to_5' },
          { label: '6-10', value: '6_to_10' },
          { label: '11-25', value: '11_to_25' },
          { label: '26-50', value: '26_to_50' },
          { label: '51-100', value: '51_to_100' },
          { label: 'Over 100', value: 'over_100' },
        ],
      },
      {
        name: 'annual-revenue',
        fieldId: 'annual_revenue',
        type: 'select',
        label: 'Annual Revenue',
        required: true,
        options: [
          { label: 'Under $100,000', value: 'under_100k' },
          { label: '$100,000 - $500,000', value: '100k_to_500k' },
          { label: '$500,000 - $1,000,000', value: '500k_to_1m' },
          { label: '$1,000,000 - $5,000,000', value: '1m_to_5m' },
          { label: 'Over $5,000,000', value: 'over_5m' },
        ],
      },
      {
        name: 'business-locations',
        fieldId: 'business_locations',
        type: 'number',
        label: 'Number of Business Locations',
        placeholder: 'Total number of locations',
        required: true,
        validation: {
          min: 1,
          max: 10000,
        },
      },
      {
        name: 'coverage-limits',
        fieldId: 'coverage_limits',
        type: 'select',
        label: 'Desired Coverage Limits',
        required: true,
        options: [
          { label: '$500,000 per occurrence', value: '500k' },
          { label: '$1,000,000 per occurrence', value: '1m' },
          { label: '$2,000,000 per occurrence', value: '2m' },
          { label: '$5,000,000 per occurrence', value: '5m' },
          { label: 'Higher limits required', value: 'higher' },
        ],
      },
      {
        name: 'professional-services',
        fieldId: 'professional_services',
        type: 'radio',
        label: 'Do you provide professional services?',
        required: true,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        name: 'product-manufacturing',
        fieldId: 'product_manufacturing',
        type: 'radio',
        label: 'Do you manufacture or sell products?',
        required: true,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        name: 'previous-claims',
        fieldId: 'previous_claims',
        type: 'radio',
        label: 'Any liability claims in the past 5 years?',
        required: true,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        name: 'additional-coverage',
        fieldId: 'additional_coverage',
        type: 'textarea',
        label: 'Additional Coverage Needs',
        placeholder: 'Describe any specific coverage requirements',
        required: false,
        validation: {
          maxLength: 500,
        },
      },
    ],
  },
];

export interface FormTemplatesProps {
  onSelectTemplate: (template: TemplateMetadata) => void;
  selectedTemplateId?: string;
}

export function FormTemplates({ onSelectTemplate, selectedTemplateId }: FormTemplatesProps) {
  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">Insurance Form Templates</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose from our specialized insurance form templates to get started quickly. Each template includes industry-specific fields and proper validation.
        </p>
      </div>
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {TEMPLATES.map((template) => {
          const isSelected = selectedTemplateId === template.id;
          
          return (
            <div
              key={template.id}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400 shadow-lg scale-105'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500'
              }`}
              onClick={() => onSelectTemplate(template)}
            >
              <div className="flex items-start justify-between">
                <h3 className={`text-lg font-medium ${
                  isSelected 
                    ? 'text-blue-900 dark:text-blue-100' 
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {template.name}
                  {isSelected && (
                    <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-full">
                      Selected
                    </span>
                  )}
                </h3>
              </div>
              <p className={`mb-4 text-sm ${
                isSelected 
                  ? 'text-blue-700 dark:text-blue-300' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {template.description}
              </p>
              <div className="space-y-1">
                <div className={`text-sm font-medium ${
                  isSelected 
                    ? 'text-blue-800 dark:text-blue-200' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}>
                  Includes:
                </div>
                <ul className={`text-sm list-disc list-inside ${
                  isSelected 
                    ? 'text-blue-600 dark:text-blue-300' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {template.fields.slice(0, 5).map((field) => (
                    <li key={field.name}>{field.label}</li>
                  ))}
                  {template.fields.length > 5 && (
                    <li className="text-xs italic">+ {template.fields.length - 5} more fields</li>
                  )}
                </ul>
              </div>
              <button
                className={`px-4 py-2 mt-4 w-full text-sm font-medium rounded-md transition-colors ${
                  isSelected
                    ? 'text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600'
                    : 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                }`}
              >
                {isSelected ? 'Template Selected' : 'Use This Template'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
} 